"use client";

import { DataProvider } from "@refinedev/core";
import { apiService } from "../services/api";

export const dataProvider = {
  getList: async ({ resource, pagination, sorters, filters, meta }) => {
    let response;
    
    // Helper function to get filter value
    const getFilterValue = (field: string) => {
      const filter = filters?.find((f: any) => f.field === field);
      return filter?.value;
    };
    
    // Handle pagination mode - when "off", fetch all records
    const getLimit = (defaultLimit: number = 10) => {
      if (pagination?.mode === "off") return 10000; // Fetch all
      return pagination?.pageSize || defaultLimit;
    };

    switch (resource) {
      case "students":
        response = await apiService.getStudents({
          limit: getLimit(10),
          ...(getFilterValue("search") && { 
            search: getFilterValue("search") 
          }),
          ...(getFilterValue("status") && { 
            status: getFilterValue("status") 
          }),
        });
        break;
        
      case "lessons":
        response = await apiService.getLessons({
          limit: getLimit(10),
          ...(getFilterValue("studentId") && { 
            studentId: getFilterValue("studentId") 
          }),
          ...(getFilterValue("startDate") && { 
            startDate: getFilterValue("startDate") 
          }),
          ...(getFilterValue("endDate") && { 
            endDate: getFilterValue("endDate") 
          }),
          ...(getFilterValue("status") && { 
            status: getFilterValue("status") 
          }),
        });
        break;
        
      case "invoices":
        response = await apiService.getInvoices({
          limit: getLimit(10),
          ...(getFilterValue("studentId") && { 
            studentId: getFilterValue("studentId") 
          }),
          ...(getFilterValue("status") && { 
            status: getFilterValue("status") 
          }),
        });
        break;
        
      case "locations":
        response = await apiService.getLocations();
        break;
        
      case "families":
        response = await apiService.getFamilies({
          limit: getLimit(50),
          ...(getFilterValue("search") && { 
            search: getFilterValue("search") 
          }),
        });
        break;
        
        
      case "tutor-profiles":
        response = await apiService.getTutorProfiles({
          limit: getLimit(10),
          ...(getFilterValue("userId") && { 
            userId: getFilterValue("userId") 
          }),
        });
        break;
        
      default:
        return { data: [] as any[], total: 0 };
    }
    
    if (response.success && response.data) {
      // Extract the actual array from the API response structure
      let data: any[] = [];
      let total: number = 0;
      
      // Check if response.data is an object with nested properties or a direct array
      const isObjectResponse = response.data && typeof response.data === 'object' && !Array.isArray(response.data);
      
      switch (resource) {
        case "students":
          data = isObjectResponse && (response.data as any).students 
            ? (response.data as any).students 
            : (Array.isArray(response.data) ? response.data : []);
          total = isObjectResponse && (response.data as any).total 
            ? (response.data as any).total 
            : data.length;
          break;
        case "lessons":
          data = isObjectResponse && (response.data as any).lessons 
            ? (response.data as any).lessons 
            : (Array.isArray(response.data) ? response.data : []);
          total = isObjectResponse && (response.data as any).summary?.total 
            ? (response.data as any).summary.total 
            : data.length;
          break;
        case "invoices":
          data = isObjectResponse && (response.data as any).invoices 
            ? (response.data as any).invoices 
            : (Array.isArray(response.data) ? response.data : []);
          total = isObjectResponse && (response.data as any).summary?.total 
            ? (response.data as any).summary.total 
            : data.length;
          break;
        case "locations":
          data = isObjectResponse && (response.data as any).locations 
            ? (response.data as any).locations 
            : (Array.isArray(response.data) ? response.data : []);
          total = isObjectResponse && (response.data as any).total 
            ? (response.data as any).total 
            : data.length;
          break;
        case "families":
          data = isObjectResponse && (response.data as any).families 
            ? (response.data as any).families 
            : (Array.isArray(response.data) ? response.data : []);
          total = isObjectResponse && (response.data as any).total 
            ? (response.data as any).total 
            : data.length;
          break;
        case "tutor-profiles":
          data = isObjectResponse && (response.data as any).data 
            ? (response.data as any).data 
            : (Array.isArray(response.data) ? response.data : []);
          total = isObjectResponse && (response.data as any).total 
            ? (response.data as any).total 
            : data.length;
          break;
        default:
          data = Array.isArray(response.data) ? response.data : [];
          total = data.length;
      }
      
      return {
        data: data,
        total: total,
      };
    }
    
    throw new Error(`Failed to fetch ${resource}: ${response.message}`);
  },

  getOne: async ({ resource, id }) => {
    let response;
    
    switch (resource) {
      case "students":
        response = await apiService.getStudent(id as string);
        break;
        
      case "lessons":
        response = await apiService.getLesson(id as string);
        break;
        
      case "invoices":
        response = await apiService.getInvoice(id as string);
        break;
        
      case "locations":
        response = await apiService.getLocation(id as string);
        break;
        
      case "families":
        response = await apiService.getFamily(id as string);
        break;
        
      case "tutor-profiles":
        response = await apiService.getTutorProfile(id as string);
        break;
        
      case "profile":
        // For profile, we don't need an ID since it's the current user's profile
        response = await apiService.getProfile();
        break;
        
      default:
        throw new Error(`Resource ${resource} not supported`);
    }
    
    if (response.success && response.data) {
      return { data: response.data as any };
    }
    
    throw new Error(`Failed to fetch ${resource} with id ${id}: ${response.message}`);
  },

  create: async ({ resource, variables }) => {
    let response;
    
    switch (resource) {
      case "students":
        response = await apiService.createStudent(variables);
        break;
        
      case "lessons":
        response = await apiService.createLesson(variables);
        break;
        
      case "invoices":
        response = await apiService.createInvoice(variables);
        break;
        
      case "locations":
        response = await apiService.createLocation(variables);
        break;
        
      case "families":
        response = await apiService.createFamily(variables as any);
        break;
        
      // Carers are now embedded in families, no separate endpoint needed
        
      case "tutor-profiles":
        response = await apiService.createTutorProfile(variables);
        break;
        
      case "profile":
        response = await apiService.createProfile(variables);
        break;
        
      default:
        throw new Error(`Create operation not supported for resource: ${resource}`);
    }
    
    if (response.success && response.data) {
      return { data: response.data };
    }
    
    throw new Error(`Failed to create ${resource}: ${response.message}`);
  },

  update: async ({ resource, id, variables }) => {
    let response;
    
    switch (resource) {
      case "students":
        response = await apiService.updateStudent(id as string, variables);
        break;
        
      case "lessons":
        response = await apiService.updateLesson(id as string, variables);
        break;
        
      case "invoices":
        response = await apiService.updateInvoice(id as string, variables);
        break;
        
      case "locations":
        response = await apiService.updateLocation(id as string, variables);
        break;
        
      case "families":
        response = await apiService.updateFamily(id as string, variables as any);
        break;
        
      // Carers are now embedded in families, no separate endpoint needed
        
      case "tutor-profiles":
        response = await apiService.updateTutorProfile(id as string, variables);
        break;
        
      case "profile":
        // For profile, we don't need an ID since it's the current user's profile
        response = await apiService.updateProfile(variables);
        break;
        
      default:
        throw new Error(`Update operation not supported for resource: ${resource}`);
    }
    
    if (response.success && response.data) {
      return { data: response.data };
    }
    
    throw new Error(`Failed to update ${resource} with id ${id}: ${response.message}`);
  },

  deleteOne: async ({ resource, id }) => {
    let response;
    
    switch (resource) {
      case "students":
        response = await apiService.deleteStudent(id as string);
        break;
        
      case "lessons":
        response = await apiService.deleteLesson(id as string);
        break;
        
      case "invoices":
        response = await apiService.deleteInvoice(id as string);
        break;
        
      case "locations":
        response = await apiService.deleteLocation(id as string);
        break;
        
      case "families":
        response = await apiService.deleteFamily(id as string);
        break;
        
      // Carers are now embedded in families, no separate endpoint needed
        
      case "tutor-profiles":
        response = await apiService.deleteTutorProfile(id as string);
        break;
        
      default:
        throw new Error(`Delete operation not supported for resource: ${resource}`);
    }
    
    if (response.success) {
      return { data: response.data || { id } };
    }
    
    throw new Error(`Failed to delete ${resource} with id ${id}: ${response.message}`);
  },

  getApiUrl: () => {
    return apiService.apiBaseUrl;
  },

  // Custom methods for tutor-specific operations
  custom: async ({ url, method, payload, query, headers }) => {
    // Handle any custom API calls using the same authenticated method as other endpoints
    const searchParams = query ? new URLSearchParams(query).toString() : '';
    const endpoint = `${url}${searchParams ? `?${searchParams}` : ''}`;
    
    const response = await apiService.customRequest(endpoint, {
      method: method || 'GET',
      ...(payload && { body: JSON.stringify(payload) }),
      headers,
    });
    
    return { data: response };
  },
} as DataProvider; 